/**
 * Recover the link between a browser's OLD analytics identities and its new one.
 *
 * WHY THIS CANNOT BE DONE IN SQL. Until 2026-09-16 the three analytics tables
 * carried three anonymous ids from three storage keys -- `sw_anonymous_id`
 * (`events`), `anon_id` (`funnel_events`) and `sw_error_anonymous_id`
 * (`error_logs`) -- and nothing anywhere recorded which belonged to which
 * person. The consolidation in `utils/tracking-context.ts` made everything
 * AFTER it agree, and left everything BEFORE it orphaned: a year of funnel and
 * error history that cannot be attached to the visitor who produced it.
 *
 * Reconstructing it from the data was measured and does not work. Matching
 * `funnel_events` sessions to `events` sessions on IP + user agent + overlapping
 * time resolved a single candidate for only **131 of 607** sessions on a sample
 * day (22%); the other 77% had between 2 and 20 candidates. Matching on
 * `user_id` was worse -- 6 unique of ~250 -- because one person has many
 * sessions. A 22% mapping of unknown precision, written into a table and joined
 * through, would produce numbers that look authoritative and are mostly absent.
 *
 * But the browser still knows. `anon_id` and `sw_error_anonymous_id` are still
 * sitting in localStorage next to the canonical `sw_anonymous_id`, because
 * nothing deleted them -- and the browser is the only party that was ever
 * present for all three. Asking it once, and writing the answer down, gives an
 * EXACT mapping rather than an inferred one.
 *
 * WHAT IT RECOVERS, AND WHAT IT DOES NOT. Anonymous ids are per browser, so this
 * links a PERSON's historical funnel and error rows to their current identity.
 * It does not recover which historical session was which: those ids lived in
 * sessionStorage and are gone with the tabs that held them. So "this visitor's
 * history" becomes answerable and "which error happened inside this 2026-09-10
 * checkout" stays unanswerable.
 *
 * THIS DECAYS. Every browser that clears storage or never comes back takes its
 * mapping with it, and no later run can recover it. Coverage is whoever
 * returns, starting from the day it ships.
 *
 * Browser only: utils/analytics.ts, the one caller, runs all three behind its
 * own `typeof window` check (a deferred setTimeout and identify()).
 */

import { getAnonymousId } from './tracking-context';
import { trackFunnelEvent } from './track-funnel';

/** Written once the pairing has been reported, so it is reported once. */
const BRIDGED_FLAG = 'sw_identity_bridged';

/** One per account, so a shared device records a link for each person who signs in. */
const LINKED_PREFIX = 'sw_identity_user_';

/** The publisher widget's link parameter: the reader's `_sw_uid` (sellwild-widget withWidgetUid). */
const WIDGET_UID_PARAM = 'sw_wuid';

/** One per widget id, so a reader who clicks through ten times is linked once. */
const WIDGET_LINKED_PREFIX = 'sw_identity_widget_';

/** The widget mints `_sw_uid` as an RFC 4122 version 4 uuid (crypto.randomUUID or its own fallback). */
const WIDGET_UID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * The retired anonymous-id keys, and the table each one wrote.
 *
 * Named by table rather than by key so the recorded event says what it is for.
 */
const LEGACY_ANON_KEYS: { key: string; table: string }[] = [
  { key: 'anon_id', table: 'funnel_events' },
  { key: 'sw_error_anonymous_id', table: 'error_logs' },
];

function read(key: string): string {
  try {
    return localStorage.getItem(key) || '';
  } catch {
    // Blocked storage: nothing to recover for this visitor, which is not an
    // error worth logging on every page load.
    return '';
  }
}

/**
 * Report this browser's retired anonymous ids once, if it has any.
 *
 * Silent when there is nothing to say -- a browser that first arrived after the
 * consolidation has no legacy id, and emitting a row to announce that would be
 * pure noise on by far the most common path.
 */
export function bridgeLegacyIdentity(): void {
  if (read(BRIDGED_FLAG)) return;

  const current = getAnonymousId();
  if (!current) return;

  const legacy = LEGACY_ANON_KEYS.map(({ key, table }) => ({ table, id: read(key) })).filter(
    // A legacy id equal to the current one was adopted rather than orphaned, so
    // there is no gap to bridge.
    ({ id }) => id && id !== current
  );

  // Mark it done either way. A browser with nothing to report must not re-check
  // on every page load for the rest of its life.
  try {
    localStorage.setItem(BRIDGED_FLAG, new Date().toISOString());
  } catch {
    // Without the flag this would fire again next load. Storage is blocked, so
    // the read above returned '' and there is nothing to report anyway.
    return;
  }

  if (legacy.length === 0) return;

  // Its own funnel name: this is not a step in anything, it is a fact about the
  // browser, and filing it under a real funnel would put a phantom session into
  // whichever funnel happened to load first.
  trackFunnelEvent('identity', 'anon_bridge', {
    // `anonymous_id` rides in the event's own column; these are the retired ones.
    legacy_funnel_anon: legacy.find((l) => l.table === 'funnel_events')?.id ?? '',
    legacy_error_anon: legacy.find((l) => l.table === 'error_logs')?.id ?? '',
  });
}

/**
 * Record that this browser belongs to a signed-in account.
 *
 * Deterministic stitching, and the only kind that actually works. An anonymous
 * id says "this browser"; a user id says "this person". Writing down the pairing
 * is what lets a seller's browsing BEFORE they signed in be attributed to them
 * afterwards -- the three days of listing views that preceded a signup are
 * otherwise a stranger's, and the funnel that led to the account cannot be
 * reconstructed at all.
 *
 * The row carries no ids of its own on purpose. `anonymous_id` comes from the
 * server's cookie and `user_id` from the server's session, both stamped at the
 * ingest boundary, so neither can be spoofed by a client and neither depends on
 * the browser being able to read an HttpOnly cookie.
 *
 * Called from `analytics.identify()`, which fires on the transition into a
 * signed-in session -- the one moment where the session cookie is guaranteed to
 * exist, so the ingest stamp cannot come back null.
 */
export function linkUserToBrowser(userId: number): void {
  if (!Number.isFinite(userId) || userId <= 0) return;

  const flag = `${LINKED_PREFIX}${userId}`;
  if (read(flag)) return;

  try {
    localStorage.setItem(flag, new Date().toISOString());
  } catch {
    // Blocked storage: emit anyway. A duplicate link row is harmless -- it
    // re-states a pairing that is already true -- while skipping the write
    // would lose the stitch entirely for this visitor.
  }

  // `linked_user_id` duplicates the column the server stamps, deliberately: if
  // the session is somehow not visible at ingest, the row still says who it was.
  trackFunnelEvent('identity', 'user_link', { linked_user_id: userId });
}

/**
 * Record that this browser is the reader a publisher widget knows by `sw_wuid`.
 *
 * The widget runs on publisher pages, where its id (`_sw_uid`) lives in the
 * publisher's storage and our `sw_anonymous_id` cookie is never sent. A click
 * through to sellwild.com is the one moment both ids are in the same place, so
 * the widget puts its id on the link and this writes the pair down. It is what
 * lets a widget reader's views and clicks (BigQuery, keyed by `_sw_uid`) be
 * joined to their sellwild.com browsing, bot score and account.
 *
 * Like `user_link`, the row's `anonymous_id` is the server's cookie, stamped at
 * ingest; only the widget id travels in `data`. The parameter is taken off the
 * address bar so a copied or shared link does not tie someone else's browser
 * to this reader; a pair claimed by two browsers should still be dropped, not
 * resolved, when it is read.
 */
export function linkWidgetToBrowser(): void {

  const url = new URL(window.location.href);
  const widgetUid = url.searchParams.get(WIDGET_UID_PARAM);
  if (widgetUid === null) return;

  url.searchParams.delete(WIDGET_UID_PARAM);
  try {
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // A sandboxed frame can refuse history writes. The link below still counts;
    // the parameter just stays visible.
  }

  if (!WIDGET_UID_SHAPE.test(widgetUid)) return;
  const id = widgetUid.toLowerCase();

  const flag = `${WIDGET_LINKED_PREFIX}${id}`;
  if (read(flag)) return;
  try {
    localStorage.setItem(flag, new Date().toISOString());
  } catch {
    // Blocked storage: emit anyway, as for user_link. A repeated pair is
    // harmless; a lost one is gone for good.
  }

  trackFunnelEvent('identity', 'widget_link', {
    widget_uid: id,
    // The widget's links name the publisher as p (and utm_source).
    widget_partner: url.searchParams.get('p') || url.searchParams.get('utm_source') || '',
  });
}
